/**
 * Asset action button primitives.
 *
 * Small presentational components reused by `AssetDetailActions` (single-asset
 * action panel) and `AssetDetailPanel` (multi-select bulk actions).
 */

import { useState } from 'react';
import { ZprTooltip } from '@zpr-stencil';
import { BinIcon, DownloadIcon } from '../../../icons';

const ICON_SIZE = 'var(--zpr-size-icon-default)';

interface DownloadAssetButtonProps {
  onDownload: () => Promise<void> | void;
  className?: string;
}

export const DownloadAssetButton = ({ onDownload, className = '' }: DownloadAssetButtonProps) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleClick = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await onDownload();
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <button
      type="button"
      aria-label="Download asset"
      className={`btn btn-ghost p0 ${isDownloading ? 'processing' : ''} ${className}`.trim()}
      onClick={handleClick}
      disabled={isDownloading}
    >
      <DownloadIcon width={ICON_SIZE} height={ICON_SIZE} />
      Download asset
    </button>
  );
};

interface MoveToBinButtonProps {
  onClick: () => void;
  /** When false, renders a disabled button wrapped in a permissions-explainer tooltip. */
  havePermissionToDelete: boolean;
  className?: string;
}

export const MoveToBinButton = ({ onClick, havePermissionToDelete, className = '' }: MoveToBinButtonProps) => {
  if (!havePermissionToDelete) {
    return (
      <ZprTooltip action="hover" placement="top">
        <button
          slot="trigger"
          type="button"
          aria-label="Move to bin"
          className={`btn btn-ghost p0 ${className}`.trim()}
          disabled
        >
          <BinIcon width={ICON_SIZE} height={ICON_SIZE} />
          Move to bin
        </button>
        <p slot="tooltip">You do not have permission to delete assets</p>
      </ZprTooltip>
    );
  }
  return (
    <button
      type="button"
      aria-label="Move to bin"
      className={`btn btn-ghost p0 ${className}`.trim()}
      onClick={onClick}
    >
      <BinIcon width={ICON_SIZE} height={ICON_SIZE} />
      Move to bin
    </button>
  );
};
